import { makeObservable, observable, action, computed, runInAction } from 'mobx';
import axios, { CancelTokenSource } from 'axios';
import { WorksheetDto, WorksheetStatus, NodeBaseDto, WorksheetSubmissionProgressDto } from '../../types';
import { NodeBaseModel, BalanceInformationNodeModel, InputBaseModel } from '..';
import { NodeFactory, InstanceUtils } from '../../misc';
import { TypeUtils } from '../../../common/misc';
import { SourceModelBase } from '../../../custom_shared/models';
import { AccountingStatementService } from '../../services';
import SessionStore from '../../stores/SessionStore';
import WorksheetsChangesStore from '../../stores/WorksheetsChangesStore';

export default class WorksheetModel {
    readonly id: string;
    readonly index: number;
    readonly startPage: number;
    readonly endPage: number;

    nodes: NodeBaseModel[] = [];

    worksheetProgress?: WorksheetSubmissionProgressDto;

    hasValidationError: boolean = false;

    hasChangedData: boolean = false;

    isSelected: boolean = false;

    isSelectedForSubmission: boolean = false;

    isSubmitting: boolean = false;

    private netDueCalculationCancelToken: CancelTokenSource | null = null;

    constructor(
        data: WorksheetDto,
        sources: SourceModelBase[],
        private readonly sessionStore: SessionStore,
        private readonly worksheetsChangesStore: WorksheetsChangesStore,
        private readonly service: AccountingStatementService
    ) {
        this.id = data.id;
        this.index = data.index;
        this.startPage = data.startPage;
        this.endPage = data.endPage;

        this.nodes = this.createOrUpdateNodes(data.nodes, [], sources);

        makeObservable(this, {
            nodes: observable,
            worksheetProgress: observable,
            hasValidationError: observable,
            hasChangedData: observable,
            setHasValidationError: action,
            setHasChangedData: action,
            isSelected: observable,
            isSelectedForSubmission: observable,
            isSubmitting: observable,
            setIsSubmitting: action,
            setIsSelected: action,
            setIsSelectedForSubmission: action,
            updateWorksheetProgress: action,
            changes: computed,
            hasChanges: computed,
            inputs: computed,
            inputsWithSource: computed,
            generalInformationNode: computed,
            balanceInformationNodes: computed,
            businessId: computed,
            netDueLabel: computed,
            isUnprocessed: computed,
            isFailed: computed,
            isProcessing: computed,
            isSuccessful: computed,
            isLocked: computed,
            inProgress: computed,
            sicsWorksheetId: computed,
            sicsBusinessId: computed,
            status: computed,
            errors: computed
        });
    }

    createOrUpdateNodes(
        data: NodeBaseDto[],
        existingNodes: NodeBaseModel[],
        sources: SourceModelBase[],
        parentNode?: NodeBaseModel
    ): NodeBaseModel[] {
        return data.map(nodeDto => {
            const existingNode = existingNodes.find(t => t.id === nodeDto.id);

            if (existingNode) {
                existingNode.update(nodeDto, sources);

                existingNode.setChildNodes(
                    nodeDto.childNodes.length
                        ? [
                              ...this.createOrUpdateNodes(
                                  nodeDto.childNodes,
                                  existingNode.childNodes,
                                  sources,
                                  existingNode
                              )
                          ]
                        : []
                );

                return existingNode;
            }

            const newNode = NodeFactory.createNode(nodeDto, sources, this, parentNode);

            if (nodeDto.childNodes.length) {
                newNode.setChildNodes([...this.createOrUpdateNodes(nodeDto.childNodes, [], sources, newNode)]);
            }

            return newNode;
        });
    }

    setHasValidationError(hasValidationError: boolean) {
        this.hasValidationError = hasValidationError;
    }

    setHasChangedData(hasChangedData: boolean) {
        this.hasChangedData = hasChangedData;
    }

    setIsSelected(isSelected: boolean) {
        this.isSelected = isSelected;
    }

    setIsSelectedForSubmission(isSelectedForSubmission: boolean) {
        this.isSelectedForSubmission = isSelectedForSubmission;
    }

    setIsSubmitting(isSubmitting: boolean) {
        this.isSubmitting = isSubmitting;
    }

    update(data: WorksheetDto, sources: SourceModelBase[]) {
        this.nodes = this.createOrUpdateNodes(data.nodes, this.nodes, sources);
        this.setHasChangedData(false);
    }

    updateWorksheetProgress(worksheetProgress: WorksheetSubmissionProgressDto) {
        runInAction(() => {
            this.worksheetProgress = worksheetProgress;

            if (this.isSubmitting) {
                this.setIsSubmitting(false);
            }
        });
    }

    getInputByGuid(guid: string) {
        return this.inputs.find(i => i.guid === guid);
    }

    async calculateNetDue(balanceNodes: BalanceInformationNodeModel[]) {
        this.cancelNetDueCalculationRequest();
        this.netDueCalculationCancelToken = axios.CancelToken.source();

        const response = await this.service.calculateNetDue(
            this.sessionStore.sessionExtension,
            this.sessionStore.sessionId,
            balanceNodes.map(node => node.dto),
            this.netDueCalculationCancelToken.token
        );

        if (response.isErr()) {
            console.error(response.error.text);
            return;
        }

        response.map(data => {
            Object.entries(data).forEach(([id, netDue]) => {
                const node = this.balanceInformationNodes.find(n => n.id === id);

                if (node) {
                    node.setNetDue(netDue);
                }
            });
        });
    }

    get changes() {
        return this.worksheetsChangesStore.worksheetsChanges[this.id] ?? [];
    }

    get hasChanges() {
        return this.changes.length > 0;
    }

    get inputs() {
        return this.nodes.flatMap(node => this.getAllInputs(node));
    }

    get inputsWithSource() {
        return this.inputs.filter(InstanceUtils.isInputWithSource);
    }

    get generalInformationNode() {
        return this.nodes.find(InstanceUtils.isGeneralInformationNode);
    }

    get balanceInformationNodes() {
        return this.nodes.filter(InstanceUtils.isBalanceInformationNode);
    }

    get businessId() {
        const businessId = this.balanceInformationNodes.find(node => node.businessIdInput?.value);
        return TypeUtils.isString(businessId?.businessIdInput?.value) ? businessId?.businessIdInput?.value : null;
    }

    get netDueLabel() {
        return this.balanceInformationNodes[0]?.netDueLabel ?? '';
    }

    get status(): WorksheetStatus {
        return this.worksheetProgress?.status ?? 'Unprocessed';
    }

    get isUnprocessed(): boolean {
        return this.status === 'Unprocessed';
    }

    get isProcessing(): boolean {
        return this.status === 'Processing';
    }

    get isSuccessful(): boolean {
        return this.status === 'Successful';
    }

    get isFailed(): boolean {
        return this.status === 'Failed';
    }

    get isLocked(): boolean {
        return this.inProgress || this.isSuccessful;
    }

    get inProgress(): boolean {
        return this.isProcessing || this.isSubmitting;
    }

    get sicsWorksheetId(): string | null {
        return this.worksheetProgress?.result?.worksheetId ?? null;
    }

    get sicsBusinessId(): string | null {
        return this.worksheetProgress?.result?.businessId ?? null;
    }

    get errors(): string[] {
        return this.worksheetProgress?.result?.errorMessage ? [this.worksheetProgress.result.errorMessage] : [];
    }

    get dto(): WorksheetDto {
        return {
            id: this.id,
            index: this.index,
            startPage: this.startPage,
            endPage: this.endPage,
            nodes: this.nodes.map(node => node.dto)
        };
    }

    private getAllInputs(node: NodeBaseModel): InputBaseModel[] {
        return [...node.inputs, ...node.childNodes.flatMap(childNode => this.getAllInputs(childNode))];
    }

    private cancelNetDueCalculationRequest() {
        if (this.netDueCalculationCancelToken) {
            this.netDueCalculationCancelToken.cancel();
            this.netDueCalculationCancelToken = null;
        }
    }
}
